// Task files on the web (TASK-FILES; Work OS's attachments panel, design 2026-10-22-task-files):
// the shared pieces of the drawer's Files section and the board/space Files tab. Every byte is
// read through `files.content` (the authenticated proxy): a preview, "open in a new tab" and a
// download are blob URLs made from it, never a link to storage. The row shows the kind's icon or
// the image's thumbnail, the name, size, uploader (an "Agent" badge for a digital employee) and
// time, and its controls as visible hover icons (preview, open, download, cover, rename, delete).
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  BotIcon,
  DownloadIcon,
  ExternalLinkIcon,
  EyeIcon,
  FileAudioIcon,
  FileIcon,
  FileImageIcon,
  FileTextIcon,
  FileVideoIcon,
  type LucideIcon,
  PencilIcon,
  StarIcon,
  Trash2Icon,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import type { Api } from "../../api/client";
import { ago } from "./doc-kinds";
import { workKeys } from "./work-queries";

export type FileItem = Awaited<ReturnType<Api["client"]["files"]["get"]>>;
export type FolderItem = Awaited<ReturnType<Api["client"]["files"]["folders"]>>["data"][number];

export const fileKeys = {
  /** Under the task's key: a task ping or a task refetch refreshes its files too. */
  task: (co: string, taskId: string) => [...workKeys.task(co, taskId), "files"] as const,
  live: (co: string, taskId: string) => [...workKeys.task(co, taskId), "files", "live"] as const,
  trash: (co: string, taskId: string) => [...workKeys.task(co, taskId), "files", "trash"] as const,
  folders: (co: string, taskId: string) =>
    [...workKeys.task(co, taskId), "files", "folders"] as const,
  place: (co: string, kind: "board" | "space", id: string) =>
    ["company", co, "work", "files", kind, id] as const,
  blob: (co: string, id: string, thumb: boolean) =>
    ["company", co, "work", "file-blob", id, thumb ? "thumb" : "full"] as const,
};

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 102.4) / 10} KB`;
  return `${Math.round(n / (1024 * 104.8576)) / 10} MB`;
}

const ICONS: Record<FileItem["preview"], LucideIcon> = {
  image: FileImageIcon,
  pdf: FileTextIcon,
  text: FileTextIcon,
  video: FileVideoIcon,
  audio: FileAudioIcon,
  none: FileIcon,
};
export const iconOf = (f: Pick<FileItem, "preview">): LucideIcon => ICONS[f.preview];

/** The file's bytes (or its thumbnail) as a blob, through the authenticated proxy. */
export async function fileBlob(
  api: Api,
  co: string,
  id: string,
  opts: { thumb?: boolean; download?: boolean } = {},
): Promise<Blob> {
  const blob = await api.client.files.content({
    params: { co, id },
    query: {
      ...(opts.thumb ? { thumb: "true" as const } : {}),
      ...(opts.download ? { download: "true" as const } : {}),
    },
  });
  return blob as Blob;
}

/** A blob URL for the file while the component lives (revoked after). */
export function useBlobUrl(api: Api, co: string, id: string | null, thumb = false) {
  const q = useQuery({
    queryKey: fileKeys.blob(co, id ?? "none", thumb),
    queryFn: () => fileBlob(api, co, id as string, { thumb }),
    enabled: id !== null,
    staleTime: 5 * 60_000,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    const u = URL.createObjectURL(q.data);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data]);
  return { url, pending: q.isPending && id !== null, error: q.isError };
}

/**
 * A board's card covers (Work OS: up to four per card): its tasks' files marked as covers that
 * have a thumbnail, as blob URLs of their 480 px thumbnails, by task id.
 */
export function useBoardCovers(
  api: Api,
  co: string,
  boardId: string,
  enabled: boolean,
): Map<string, string[]> {
  const list = useQuery({
    queryKey: [...fileKeys.place(co, "board", boardId), "covers"],
    queryFn: () =>
      api.client.files.list({ params: { co }, query: { board_id: boardId, limit: 200 } }),
    enabled,
    staleTime: 30_000,
  });
  const covers = (list.data?.data ?? []).filter((f) => f.is_cover && f.has_thumb && f.task);
  const blobs = useQueries({
    queries: covers.map((f) => ({
      queryKey: fileKeys.blob(co, f.id, true),
      queryFn: () => fileBlob(api, co, f.id, { thumb: true }),
      staleTime: 5 * 60_000,
    })),
  });
  const sig = covers.map((f, i) => `${f.id}:${blobs[i]?.dataUpdatedAt ?? 0}`).join(",");
  const [urls, setUrls] = useState<Map<string, string[]>>(() => new Map());
  // biome-ignore lint/correctness/useExhaustiveDependencies: `sig` names the covers and their blobs
  useEffect(() => {
    const made: string[] = [];
    const next = new Map<string, string[]>();
    covers.forEach((f, i) => {
      const blob = blobs[i]?.data;
      const task = f.task?.id;
      if (!blob || !task) return;
      const url = URL.createObjectURL(blob);
      made.push(url);
      const have = next.get(task) ?? [];
      if (have.length < 4) next.set(task, [...have, url]);
    });
    setUrls(next);
    return () => {
      for (const u of made) URL.revokeObjectURL(u);
    };
  }, [sig]);
  return urls;
}

/** Opens the file in a new tab (a blob URL: the proxy's bytes, typed as served). */
export async function openInTab(api: Api, co: string, f: Pick<FileItem, "id">) {
  // The tab opens on the click (a popup after an await is blocked), then gets its URL.
  const tab = window.open("", "_blank");
  const blob = await fileBlob(api, co, f.id);
  const url = URL.createObjectURL(blob);
  if (tab) tab.location.href = url;
  else window.location.assign(url);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Saves the file under its name (`?download=true`). */
export async function downloadFile(api: Api, co: string, f: Pick<FileItem, "id" | "file_name">) {
  const blob = await fileBlob(api, co, f.id, { download: true });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = f.file_name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function IconAction({
  label,
  icon: Icon,
  onClick,
  pressed,
  className,
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  pressed?: boolean;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            {...(pressed !== undefined ? { "aria-pressed": pressed } : {})}
            className={className}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          />
        }
      >
        <Icon aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** Shown on hover or focus of the row, and always on touch. */
export const ON_HOVER =
  "opacity-0 group-hover/file:opacity-100 group-focus-within/file:opacity-100 pointer-coarse:opacity-100";

export function FileThumb({ api, co, file }: { api: Api; co: string; file: FileItem }) {
  const { url } = useBlobUrl(api, co, file.has_thumb ? file.id : null, true);
  // WAVE12-FIX: an image the browser cannot draw falls back to the file's icon, never a broken one.
  const [failed, setFailed] = useState<string | null>(null);
  const Icon = iconOf(file);
  return (
    <span
      data-slot="file-thumb"
      className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border-subtle bg-sunken text-secondary"
    >
      {url && failed !== url ? (
        <img src={url} alt="" className="size-full object-cover" onError={() => setFailed(url)} />
      ) : (
        <Icon aria-hidden="true" className="size-4" />
      )}
    </span>
  );
}

export function UploaderLine({ file, extra }: { file: FileItem; extra?: ReactNode }) {
  const agent = file.uploader.kind !== "person";
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <span className="tabular-nums">{formatBytes(file.bytes)}</span>
      <span aria-hidden="true">·</span>
      <span className="truncate">{file.uploader.name}</span>
      {agent ? (
        <Badge variant="secondary" className="h-4 gap-0.5 px-1 text-[10px]" data-slot="agent-badge">
          <BotIcon aria-hidden="true" className="size-2.5" />
          Agent
        </Badge>
      ) : null}
      <span aria-hidden="true">·</span>
      <time dateTime={file.created_at} className="shrink-0">
        {ago(file.created_at)}
      </time>
      {extra}
    </span>
  );
}

export type FileRowActions = {
  onPreview: (f: FileItem) => void;
  onRename?: (f: FileItem) => void;
  onDelete?: (f: FileItem) => void;
  onCover?: (f: FileItem) => void;
};

export function FileRow({
  api,
  co,
  file,
  actions,
  context,
}: {
  api: Api;
  co: string;
  file: FileItem;
  actions: FileRowActions;
  /** Under the name: where it lives (a board's Files tab names the task). */
  context?: ReactNode;
}) {
  return (
    <li
      data-slot="file-row"
      data-file={file.file_name}
      className="group/file relative flex min-h-12 items-center gap-2.5 rounded-md px-1.5 py-1 hover:bg-muted"
    >
      <FileThumb api={api} co={co} file={file} />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          className="flex max-w-full items-center gap-1.5 truncate text-left text-sm font-medium text-foreground hover:underline"
          onClick={() => actions.onPreview(file)}
        >
          <span className="truncate">{file.file_name}</span>
          {file.is_cover ? (
            <StarIcon aria-label="Cover" className="size-3 shrink-0 fill-current text-warning" />
          ) : null}
        </button>
        <UploaderLine
          file={file}
          extra={
            file.message_id ? (
              <>
                <span aria-hidden="true">·</span>
                <span>in a comment</span>
              </>
            ) : null
          }
        />
        {context}
      </div>
      <div className={cn("flex shrink-0 items-center gap-0.5", ON_HOVER)}>
        <IconAction label="Preview" icon={EyeIcon} onClick={() => actions.onPreview(file)} />
        <IconAction
          label="Open in new tab"
          icon={ExternalLinkIcon}
          onClick={() => void openInTab(api, co, file)}
        />
        <IconAction
          label="Download"
          icon={DownloadIcon}
          onClick={() => void downloadFile(api, co, file)}
        />
        {actions.onCover && file.preview === "image" ? (
          <IconAction
            label={file.is_cover ? "Remove cover" : "Make cover"}
            icon={StarIcon}
            pressed={file.is_cover}
            onClick={() => actions.onCover?.(file)}
          />
        ) : null}
        {actions.onRename ? (
          <IconAction label="Rename" icon={PencilIcon} onClick={() => actions.onRename?.(file)} />
        ) : null}
        {actions.onDelete ? (
          <IconAction
            label="Delete"
            icon={Trash2Icon}
            className="hover:text-destructive"
            onClick={() => actions.onDelete?.(file)}
          />
        ) : null}
      </div>
    </li>
  );
}

/** The preview: an image, a PDF, a player or the text, with open and download beside it. */
export function PreviewDialog({
  api,
  co,
  file,
  onClose,
}: {
  api: Api;
  co: string;
  file: FileItem | null;
  onClose: () => void;
}) {
  const id = file && file.preview !== "text" && file.preview !== "none" ? file.id : null;
  const blob = useBlobUrl(api, co, id);
  const text = useQuery({
    queryKey: [...fileKeys.blob(co, file?.id ?? "none", false), "text"],
    queryFn: () => api.client.files.text({ params: { co, id: file?.id as string } }),
    enabled: file?.preview === "text",
  });
  if (!file) return null;
  const body = () => {
    if (file.preview === "none")
      return (
        <p className="text-sm text-muted-foreground">
          No preview for this kind of file. Download it to open it.
        </p>
      );
    if (file.preview === "text") {
      if (text.isPending) return <Spinner />;
      return (
        <pre className="max-h-[60vh] overflow-auto rounded-md bg-sunken p-3 text-xs whitespace-pre-wrap">
          {text.data?.text ?? ""}
        </pre>
      );
    }
    if (!blob.url)
      return blob.error ? <p className="text-sm">The file could not be read.</p> : <Spinner />;
    if (file.preview === "image")
      return (
        <img
          src={blob.url}
          alt={file.file_name}
          className="mx-auto max-h-[65vh] rounded-md object-contain"
        />
      );
    if (file.preview === "pdf")
      return (
        <iframe
          title={file.file_name}
          src={blob.url}
          className="h-[65vh] w-full rounded-md border"
        />
      );
    if (file.preview === "video")
      // biome-ignore lint/a11y/useMediaCaption: user files carry no captions
      return <video src={blob.url} controls className="max-h-[65vh] w-full rounded-md" />;
    // biome-ignore lint/a11y/useMediaCaption: user files carry no captions
    return <audio src={blob.url} controls className="w-full" />;
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-slot="file-preview">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{file.file_name}</DialogTitle>
          <DialogDescription render={<div />}>
            <UploaderLine file={file} />
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-24">{body()}</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => void openInTab(api, co, file)}>
            <ExternalLinkIcon aria-hidden="true" />
            Open in new tab
          </Button>
          <Button onClick={() => void downloadFile(api, co, file)}>
            <DownloadIcon aria-hidden="true" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Rename (a `/` in the name moves the file into that folder chain). */
export function RenameDialog({
  title,
  initial,
  hint,
  onSave,
  onClose,
  pending,
}: {
  title: string;
  initial: string;
  hint?: string;
  onSave: (name: string) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [name, setName] = useState(initial);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="file-rename">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) onSave(name.trim());
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {hint ? <DialogDescription>{hint}</DialogDescription> : null}
          </DialogHeader>
          <Input
            aria-label="Name"
            value={name}
            autoFocus
            onFocus={(e) => {
              const dot = e.target.value.lastIndexOf(".");
              e.target.setSelectionRange(0, dot > 0 ? dot : e.target.value.length);
            }}
            onChange={(e) => setName(e.target.value)}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || pending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
